import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { gettext } from '../../translations';
import moment from 'js-utils/moment';
import classNames from 'classnames';

import { STYLE_BASIC } from 'eventbrite_design_system/inputField/constants';
import DateRangePicker from 'eventbrite_design_system/datePicker/DateRangePicker';
import { START_DATE } from 'eventbrite_design_system/datePicker/constants';
import { ISO_8601_DATE_FORMAT } from 'eventbrite_design_system/utils/date';
import { constants as destinationConstants } from '@eb/destination-utils';
import interpolate from 'js-utils/interpolate';

import { formatDateRangeStringForDisplay } from './utils';

import DateSelect from './DateSelect';
import DateInputTag from './DateInputTag';

import './DateInput.scss';

const { CUSTOM_DATE, FORMATTED_DATE_MAPPING } = destinationConstants;

const DATEPICKER_DEFAULT_DISPLAY_FORMAT = 'MMM D';

const DateFilter = ({
    id = 'date-select',
    label = interpolate(gettext('Date')),
    handleClear,
    handleSelect,
    isBotRequest,
    isCustomDateSelected,
    onCancelPicker,
    onDatePickerBlur,
    shouldShowPicker,
    value,
}) => {
    let component = (
        <DateSelect
            handleClear={handleClear}
            handleSelect={handleSelect}
            isBotRequest={isBotRequest}
            isCustomDateSelected={isCustomDateSelected}
            style={STYLE_BASIC}
            id={id}
            label={label}
            value={value}
            values={[{ display: gettext('Any date'), value: '' }].concat(
                FORMATTED_DATE_MAPPING,
            )}
        />
    );

    if (shouldShowPicker) {
        component = (
            <DateInputTag
                showOverflow={true}
                isStatic={true}
                isClosable={true}
                onClose={onCancelPicker}
            >
                <DateRangePicker
                    onBlur={onDatePickerBlur}
                    inputDisplayFormat={DATEPICKER_DEFAULT_DISPLAY_FORMAT}
                    initialFocusedInput={START_DATE}
                    minDate={moment().format(ISO_8601_DATE_FORMAT)}
                    inputStartDatePlaceholder={gettext('Start')}
                    inputEndDatePlaceholder={gettext('End')}
                />
            </DateInputTag>
        );
    }

    return component;
};

export default class DateInput extends PureComponent {
    static propTypes = {
        id: PropTypes.string,
        label: PropTypes.string,
        trackGAEvent: PropTypes.func,
        selectedDate: PropTypes.string,
        isBotRequest: PropTypes.bool,
        isCustomDateSelected: PropTypes.bool,
        onChangeValue: PropTypes.func,
        date: PropTypes.string,
        dates: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                startDate: PropTypes.string,
                endDate: PropTypes.string,
            }),
        ]),
    };

    constructor(props) {
        super(props);

        this.state = {
            shouldShowPicker: false,
            isCustomDateSelected: false,
        };
    }

    _onHandleSelectDate(selectedFilter) {
        const { onChangeValue } = this.props;

        if (selectedFilter === CUSTOM_DATE) {
            return this.setState({ shouldShowPicker: true });
        }

        return onChangeValue('dates', selectedFilter);
    }

    _onHandleClear(filterType) {
        const { onChangeValue } = this.props;

        this.setState({ isCustomDateSelected: false });

        onChangeValue(filterType, '');
    }

    _handleDatePickerBlur = ({ startDate, endDate }) => {
        const { onChangeValue } = this.props;

        if (startDate && endDate) {
            this.setState({
                shouldShowPicker: false,
                isCustomDateSelected: true,
            });
        } else {
            this._handleCancelDatePicker();
        }

        onChangeValue('dates', { startDate, endDate });
    };

    _handleCancelDatePicker = () => {
        this.setState({
            isCustomDateSelected: false,
            shouldShowPicker: false,
        });
    };

    render() {
        const { shouldShowPicker, isCustomDateSelected } = this.state;

        const { id, label, dates } = this.props;

        let date = dates.date;

        const wrapperClassName = classNames('home-date-input', {
            'home-date-input--overflow': !shouldShowPicker,
        });

        if (dates.startDate || dates.endDate) {
            date = formatDateRangeStringForDisplay(dates);
        }

        return (
            <div className={wrapperClassName} data-spec="date-input__content">
                <DateFilter
                    id={id}
                    label={label}
                    handleSelect={this._onHandleSelectDate.bind(this)}
                    value={date}
                    handleClear={this._onHandleClear.bind(this)}
                    isCustomDateSelected={isCustomDateSelected}
                    shouldShowPicker={shouldShowPicker}
                    onDatePickerBlur={this._handleDatePickerBlur}
                    onCancelPicker={this._handleCancelDatePicker}
                />
            </div>
        );
    }
}

export { DateInput, DateFilter };
